// Hayah-scoped haptics wrapper.
//
// Every Hayah surface (Ghidha, Riyadah, HayahHub, HayahToday, HayahAICoach,
// HayahSettings, GhidhaImmersive, GhidhaLogView) imports from here instead of
// directly from "./haptics". All helpers gate on:
//   • hapticsEnabled  (global master, shared with non-Hayah surfaces)
//   • hapticsHayah    (Hayah-specific sub-toggle, default on)
// Disabling either silences the call.
//
// The helper names follow intent vocabulary so call sites describe meaning,
// not physical sensation:
//
//   hapticTap            — non-destructive button, open modal, expand card
//   hapticConfirm        — weighted commit (log meal, save preset, send)
//   hapticMilestone      — significant state change (workout done, all sets,
//                          danger-button hold commit, daily goal)
//   hapticWarning        — destructive intent (discard, reset, delete confirm)
//   hapticError          — log failed, API error, validation failure
//   hapticHayahSuccess   — async completion (meal logged, response loaded)
//   hapticHayahSelection — discrete pick (chip, slot, day cell, preset)
//   hapticHayahToggle    — toggle switch flip
//   hapticSelectionChange— continuous tick (stepper, slider) — throttled ~50ms
//   hapticSelectionEnd   — end of continuous gesture
//   hapticHayahSwipe     — swipe threshold crossed
//   hapticHayahLongPress — long-press recognised (commit fires hapticMilestone)
//
// All helpers degrade gracefully on web (no-op via isNative guard inside the
// underlying ./haptics primitives) and are individually try/catch-safe — a
// haptics failure never breaks the calling action.

import { isNative } from "./capacitor";
import {
  hapticImpact,
  hapticSelection,
  hapticNotify,
} from "./haptics";

const READ = (key, fallback = true) => {
  try {
    const v = localStorage.getItem(key);
    if (v === null) return fallback;
    return v !== "false";
  } catch { return fallback; }
};

const masterOn = () => READ("hapticsEnabled", true);
const hayahOn = () => masterOn() && READ("hapticsHayah", true);

// Throttle a void-returning fn so rapid invocations within `ms` collapse to a
// single call. Used for stepper / slider haptics where holding a + button
// would otherwise fire dozens of buzzes per second.
function throttle(fn, ms) {
  let last = 0;
  return (...args) => {
    const now = Date.now();
    if (now - last < ms) return;
    last = now;
    return fn(...args);
  };
}

const safe = (fn) => async (...args) => {
  if (!hayahOn()) return;
  try { return await fn(...args); } catch {}
};

// Intent-named helpers
export const hapticTap         = safe(() => hapticImpact("light"));
export const hapticConfirm     = safe(() => hapticImpact("medium"));
export const hapticMilestone   = safe(() => hapticImpact("heavy"));
export const hapticWarning     = safe(() => hapticNotify("warning"));
export const hapticError       = safe(() => hapticNotify("error"));
export const hapticHayahSuccess   = safe(() => hapticNotify("success"));
export const hapticHayahSelection = safe(() => hapticSelection());
export const hapticHayahToggle    = safe(() => hapticSelection());
export const hapticHayahSwipe     = safe(() => hapticImpact("light"));
export const hapticHayahLongPress = safe(() => hapticImpact("medium"));

// Continuous-selection tick (selectionChanged on iOS). Hits the Capacitor
// plugin directly so we get the lighter "changed" feel rather than the full
// start→changed→end sequence — appropriate for steppers/sliders. Throttled
// ~50ms so a held + button doesn't fire dozens of buzzes per second.
let _capModule = null;
let _capLoadAttempted = false;
async function loadCap() {
  if (_capLoadAttempted) return _capModule;
  _capLoadAttempted = true;
  try { _capModule = await import("@capacitor/haptics"); } catch { _capModule = null; }
  return _capModule;
}
if (typeof window !== "undefined" && isNative()) { loadCap(); }

const _vibrate = (ms) => {
  try { if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(ms); } catch {}
};

const _selectionChangeImpl = safe(async () => {
  if (isNative()) {
    const cap = await loadCap();
    if (cap?.Haptics?.selectionChanged) {
      try { await cap.Haptics.selectionChanged(); return; } catch {}
    }
    if (cap?.Haptics?.impact) {
      try { await cap.Haptics.impact({ style: cap.ImpactStyle?.Light }); return; } catch {}
    }
  }
  _vibrate(6);
});
export const hapticSelectionChange = throttle(_selectionChangeImpl, 50);

export const hapticSelectionEnd = safe(async () => {
  if (isNative()) {
    const cap = await loadCap();
    if (cap?.Haptics?.selectionEnd) {
      try { await cap.Haptics.selectionEnd(); return; } catch {}
    }
  }
});
